import type { AdminUsesSortField, AdminUsesSortOrder, UsesQueryParams } from '@queries/admin-uses.query';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useMemo } from 'react';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORTING: SortingState = [{ desc: false, id: 'name' }];

const COLUMN_ID_TO_SORT_FIELD: Record<string, AdminUsesSortField> = {
  name: 'NAME',
  status: 'STATUS',
};

type UseAdminUsesQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  handleLimitChange: (newLimit: number) => void;
  page: number;
  queryParams: UsesQueryParams;
  search: string;
  selectedUseId: string | null;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string) => Promise<URLSearchParams>;
  setSelectedUseId: (useId: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState) => Promise<URLSearchParams>;
  sortingState: SortingState;
};

export const useAdminUsesQueryParams = (): UseAdminUsesQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''));
  const [selectedUseId, setSelectedUseId] = useQueryState('useId', parseAsString);
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const columnId = sortingState[0]?.id || 'name';
  const sortField = COLUMN_ID_TO_SORT_FIELD[columnId] ?? 'NAME';
  const sortOrder: AdminUsesSortOrder = sortingState[0]?.desc ? 'DESC' : 'ASC';

  const queryParams = useMemo<UsesQueryParams>(() => {
    return {
      limit: limit ?? DEFAULT_PAGE_SIZE,
      order: sortOrder,
      page: page ?? 1,
      search: search || undefined,
      sort: sortField,
    };
  }, [limit, page, search, sortField, sortOrder]);

  const handleLimitChange = (newLimit: number) => {
    void setLimit(newLimit);
    void setPage(1);
  };

  return {
    DEFAULT_SORTING,
    handleLimitChange,
    page: page ?? 1,
    queryParams,
    search: search ?? '',
    selectedUseId,
    setPage,
    setSearch,
    setSelectedUseId,
    setSorting,
    sortingState,
  };
};
